import { useMemo } from "react";
import PageShell from "@/components/PageShell";
import { engagementRoles, socialLinks } from "@/data/team";
import { openRoles, roleCriteria, applyHref, CAREERS_EMAIL } from "@/data/careers";
import { trackCareersApplyClick } from "@/lib/analytics";
import HeroFields from "@/components/HeroFields";

const howItWorks = [
  ["Senior only", "You are in the first call and at handover; no layers above you."],
  ["One accountable team", "Product, AI, systems and operations together, no handoffs."],
  ["Measured work", "Every engagement has a baseline and weekly numbers; you see whether it worked."],
  ["Remote, worldwide", "On-site for diagnostics where the operation needs it."],
];

const hiring = [
  ["01", "A 30-minute conversation about work you have shipped."],
  ["02", "A working session on a real operating problem, paid."],
  ["03", "Reference conversations, then an engagement offer."],
];

const Careers = () => {
  const structuredData = useMemo(() => {
    const sameAs = Object.values(socialLinks).filter(Boolean);
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Skandor Technologies",
      url: "https://www.skandortech.ai",
      logo: "https://www.skandortech.ai/favicon.ico",
      ...(sameAs.length ? { sameAs } : {}),
    };
  }, []);

  return (
    <PageShell
      title="Work with us | Skandor Technologies"
      description="Skandor staffs every engagement with senior practitioners who have shipped enterprise-grade products and stay through handover. Roles, how we hire, and how to introduce yourself."
      canonicalPath="/careers"
      structuredData={structuredData}
    >
      <section className="relative overflow-hidden bg-background py-16 md:py-24">
        <HeroFields compact />
        <div className="container relative z-10 mx-auto">
          <div className="max-w-4xl">
            <p className="eyebrow">Careers</p>
            <h1 className="mt-4">Senior practitioners who want to own the outcome, not the deliverable.</h1>
            <p className="lede mt-5">Skandor staffs every engagement with people who have shipped enterprise-grade products and are willing to stay through handover. If that is how you like to work, we would like to hear from you.</p>
          </div>
        </div>
      </section>

      <section className="section-tint py-16 md:py-24">
        <div className="container mx-auto">
          <h2>How engagements work for you</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {howItWorks.map(([lead, body]) => (
              <article key={lead} className="surface-card p-6">
                <h3>{lead}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-2">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-16 md:py-24">
        <div className="container mx-auto">
          <h2>Roles we staff</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 min-[1000px]:grid-cols-5">
            {engagementRoles.map(([key, role, body]) => (
              <article key={role} className="surface-card p-5">
                <p className="font-mono text-[.66rem] text-steel-2">{key}</p>
                <h3 className="mt-3">{role}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-2">{body}</p>
                <p className="mt-4 font-mono text-[.62rem] text-steel-2">WHAT WE LOOK FOR</p>
                <p className="mt-1 text-sm leading-relaxed text-ink-2">{roleCriteria[role]}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tint py-16 md:py-24">
        <div className="container mx-auto">
          <h2>Open roles</h2>
          <p className="mt-4 max-w-3xl text-ink-2">Roles open as engagements are confirmed. Send a short note and a link to work you have shipped.</p>
          <ul className="mt-8 grid max-w-5xl gap-4">
            {openRoles.map((r) => (
              <li key={r.role} className="surface-card flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3>{r.role}</h3>
                  <p className="mt-1 font-mono text-[.66rem] text-steel-2">{r.meta}</p>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-2">{r.summary}</p>
                </div>
                <a
                  href={applyHref(r.role)}
                  onClick={() => trackCareersApplyClick(r.role)}
                  className="inline-flex min-h-11 flex-none items-center justify-center rounded-md border border-line bg-background px-5 text-sm font-medium text-steel-2 hover:border-steel-2"
                >
                  Apply
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-background py-16 md:py-24">
        <div className="container mx-auto">
          <h2>How we hire</h2>
          <ol className="mt-8 grid gap-4 md:grid-cols-3">
            {hiring.map(([n, body]) => (
              <li key={n} className="surface-card p-6">
                <span className="font-mono text-sm text-steel-2">{n}</span>
                <p className="mt-3 text-ink-2">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section-tint pb-16 pt-4 md:pb-24">
        <div className="container mx-auto">
          <div className="gradient-frame mx-auto max-w-3xl"><div className="surface-card p-8 text-center md:p-10">
            <h2>Not a fit for an open role? Introduce yourself anyway.</h2>
            <p className="mt-4 text-ink-2">Tell us what you have shipped and the kind of operating problem you want next.</p>
            <a
              href={applyHref("General introduction")}
              onClick={() => trackCareersApplyClick("General introduction")}
              className="mt-6 inline-flex min-h-11 items-center justify-center font-medium text-steel-2 underline underline-offset-4"
            >
              {CAREERS_EMAIL}
            </a>
          </div></div>
        </div>
      </section>
    </PageShell>
  );
};

export default Careers;
